import React, { Fragment as F } from 'react'
import c from 'classnames'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import groupBy from 'lodash/groupBy'
import { connect } from 'react-redux'
import moment from 'moment'
import { Flipper, Flipped } from 'react-flip-toolkit'

import Loading from 'discada/dist/Loading'
import Icon from 'discada/dist/Icon'
import { RawExpando } from 'discada/dist/Expando'

import { getEventsForQuote } from 'selectors/naughtySelectors'
import { addToPageState } from 'actions/pageStateActions'
import PricingCard from 'components/VRP/PricingCard'
import {
  money,
  moneyShort,
  moneyRange,
  calendarFormats,
  getPaymentRange,
} from 'utils'

import {
  EVENT_LABELS,
  EVENT_ICON,
  EVENT_RENDERER,
  HIGHLIGHT,
} from './naughtyBitLookups'

import { APPT_LABELS } from 'components/widgets/Scheduler'

import './NaughtyBits.scss'

export const ApptRenderer = ({ appt }) => {
  if (!appt || isEmpty(appt)) return null

  return (
    <p>
      <Icon icon="calendar" /> {appt.date}, {appt.time},{' '}
      {APPT_LABELS[appt.type]}
    </p>
  )
}
export const CommentsRenderer = ({ comments }) => {
  if (!comments || isEmpty(comments)) return null

  return (
    <p>
      <Icon icon="comment" /> {comments}
    </p>
  )
}

export function NaughtyBit(props) {
  let payment = get(props, 'payload.payment', {}),
    appointment = get(props, 'payload.appointment'),
    comments = get(props, 'payload.customerComments'),
    eventType = get(props, 'type'),
    { addToPageState, showVehicle, noLink } = props,
    {
      leasePayment,
      loanPayment,
      cashPrice,
      mode,
      dirty,
      showRange,
      paymentRangePercent,
      paymentRoundTo,
    } = payment || {},
    { stockNumber, year, make, model, trim } = props.vehicle || {},
    leadId = get(props, 'browser.tag.sessionId'),
    highlight = HIGHLIGHT[eventType] && HIGHLIGHT[eventType](props)

  if (leasePayment) {
    leasePayment = showRange
      ? moneyRange(
          getPaymentRange(leasePayment, paymentRoundTo, paymentRangePercent),
        )
      : money(leasePayment)
  }

  if (loanPayment) {
    loanPayment = showRange
      ? moneyRange(
          getPaymentRange(loanPayment, paymentRoundTo, paymentRangePercent),
        )
      : money(loanPayment)
  }

  let ret = (
    <div
      className={c('naughty-bit', {
        dirty,
        noLink,
        'highlight-bit': highlight,
      })}
    >
      {highlight && (
        <Icon icon="carrot" fixedWidth className="notification-icon" />
      )}

      {EVENT_ICON[eventType] ? (
        <div className="event-icon">{EVENT_ICON[eventType](props)}</div>
      ) : null}

      <div className="event-details">
        <h5>
          {EVENT_LABELS[eventType] ? EVENT_LABELS[eventType](props) : eventType}
        </h5>
        {EVENT_RENDERER[eventType] ? EVENT_RENDERER[eventType](props) : null}

        <div className="naughty-vehicle-info">
          {showVehicle && stockNumber && (
            <F>
              <strong>#{stockNumber}</strong> {year} {make} {model} {trim}{' '}
            </F>
          )}
        </div>
        <ApptRenderer appt={appointment} />
        <CommentsRenderer comments={comments} />
      </div>
      <div className="pricing-cards-wrapper">
        {dirty && <span className="quote-dirty">Refresh Required</span>}
        <F>
          <PricingCard
            mini
            mode="lease"
            showRange={showRange}
            payment={leasePayment || 'N/A'}
            className={c({ selected: mode === 'lease', na: !leasePayment })}
            raw
          />
          <PricingCard
            mini
            mode="loan"
            showRange={showRange}
            payment={loanPayment || 'N/A'}
            className={c({ selected: mode === 'loan', na: !loanPayment })}
            raw
          />
          <PricingCard
            mini
            short
            showPrice
            mode="cash"
            showRange={showRange}
            payment={cashPrice ? moneyShort(cashPrice) : 'N/A'}
            className={c({ selected: mode === 'cash', na: !cashPrice })}
            raw
          />
        </F>
      </div>
    </div>
  )

  if (leadId && !noLink) {
    ret = (
      <div
        onClick={() =>
          addToPageState({
            leadId,
          })
        }
      >
        {ret}
      </div>
    )
  }

  return ret
}

export function NaughtyBits(props) {
  let { className, events, isLoading, showAll, showNoneMessage } = props,
    eventCount = Object.keys(events).length,
    //eslint-disable-next-line
    nb = groupBy(Object.values(events), e => {
      let customer = !isEmpty(get(e, 'browser.tag.firstName'))
          ? get(e, 'browser.tag')
          : get(e, 'customer'),
        minute = moment(e.at)
          .startOf('minute')
          .valueOf(),
        name = get(customer, 'firstName')
          ? `${get(customer, 'firstName', '')} ${get(
              customer,
              'lastName[0]',
              '',
            )}.`
          : null

      return [minute, name].filter(x => x).join('_')
    })

  if (isLoading) return <Loading />

  let ret =
    Object.keys(events).length === 0 ? (
      showNoneMessage ? (
        <div className="no-events">No illuminations yet</div>
      ) : null
    ) : (
      Object.keys(nb)
        .sort()
        .reverse()
        .map(k => {
          let ks = k.split('_'),
            date = get(ks, '[0]'),
            name = get(ks, '[1]'),
            unknown = !name,
            browser = get(nb[k], '[0].browser', {}),
            browserInfo

          name = name || <span className="unknown-name">Anonymous</span>

          if (browser.name && browser.os)
            browserInfo = (
              <span className="browser-info">
                {browser.name} on {browser.os}
              </span>
            )

          return (
            <Flipped flipId={ks} key={ks}>
              <div className="naughty-day">
                <div className="naughty-day-meta">
                  <div className="naughty-point">
                    <span
                      className={c('naughty-point-who', {
                        'unknown-customer': unknown,
                      })}
                    >
                      <Icon icon="user-circle" /> {name}
                    </span>
                    <span>
                      {moment(parseInt(date, 10)).calendar(
                        null,
                        calendarFormats,
                      )}
                    </span>{' '}
                    {browserInfo}
                  </div>
                </div>
                <div className="naughty-day-data">
                  <ul>
                    <Flipper flipId={nb[k].map(a => a.at).join()}>
                      {nb[k]
                        //.sort((a, b) => +b.at - +a.at)
                        .map((e, kk) => (
                          <Flipped flipId={e.at} key={e.id}>
                            <li>
                              <NaughtyBit
                                {...e}
                                id={kk}
                                showVehicle={props.showVehicle}
                                noLink={props.noLink}
                                addToPageState={props.addToPageState}
                              />
                            </li>
                          </Flipped>
                        ))}
                    </Flipper>
                  </ul>
                </div>
              </div>
            </Flipped>
          )
        })
    )

  return (
    <div className={c('naughty-bits', className)}>
      <RawExpando>
        {({ toggle, open }) => (
          <div
            className={c('interactions-wrapper', {
              'interactions-wrapper-open': open || showAll,
            })}
          >
            <Flipper flipKey={Object.keys(events).join()}>{ret}</Flipper>

            {!open && eventCount > 0 && !showAll && (
              <div className="interaction-expander" onClick={toggle}>
                Show all {eventCount} illuminations <Icon icon="chevron-down" />
              </div>
            )}
          </div>
        )}
      </RawExpando>
    </div>
  )
}

function mapStateToProps(state, op) {
  return {
    ...op,
    events: getEventsForQuote(state, op.quoteId),
  }
}

export default connect(
  mapStateToProps,
  { addToPageState },
)(NaughtyBits)
